// Imports plain 'three', never '@phdsoft/three': this module also runs inside the edges Web
// Worker (light-model-edges.worker.ts), where the patched entry point cannot load (it reads window).
import { BufferAttribute, BufferGeometry, EdgesGeometry } from 'three';

/**
 * Crease angle, in degrees, for the light-model edges: an edge is drawn where the normals of its
 * two faces differ by more than this, plus every open (one-face) edge; coplanar seams are skipped.
 * In-page spike on the stage light model (165 flat meshes, ~196k triangles, GLTFExporter, no line
 * objects; 2026-10-30): `new THREE.EdgesGeometry(mesh.geometry, 30)` per mesh gave 46k segments
 * in 647 ms on the main thread for the whole model, with clean outlines (no zig-zag).
 */
export const LIGHT_MODEL_EDGE_ANGLE_DEG = 30;

export type EdgeIndexArray = Uint8Array | Uint16Array | Uint32Array;

/** One light-model mesh to process: copies of its position (xyz) and index arrays. */
export interface LightModelEdgesRequestItem {
    id: number;
    position: Float32Array;
    index: EdgeIndexArray | null;
}

/** Main thread → worker. */
export interface LightModelEdgesRequest {
    jobId: number;
    thresholdAngleDeg: number;
    items: LightModelEdgesRequestItem[];
}

/** Edge segments of one mesh, in the mesh's local space (2 vertices × 3 floats per segment). */
export interface LightModelEdgesResultItem {
    id: number;
    positions: Float32Array;
}

/** Worker → main thread. */
export interface LightModelEdgesResponse {
    jobId: number;
    results: LightModelEdgesResultItem[];
}

/**
 * Crease edges of one triangle mesh: `THREE.EdgesGeometry(geometry, thresholdAngleDeg)` on a
 * geometry built from the given arrays. Returns the LineSegments positions (local space).
 */
export function computeEdgePositions(
    position: Float32Array,
    index: EdgeIndexArray | null,
    thresholdAngleDeg: number = LIGHT_MODEL_EDGE_ANGLE_DEG,
): Float32Array {
    if (!position || position.length < 9) return new Float32Array(0);
    const geometry = new BufferGeometry();
    geometry.setAttribute('position', new BufferAttribute(position, 3));
    if (index && index.length > 0) geometry.setIndex(new BufferAttribute(index, 1));
    const edges = new EdgesGeometry(geometry, thresholdAngleDeg);
    const positions = edges.getAttribute('position').array as Float32Array;
    geometry.dispose();
    edges.dispose();
    return positions;
}

/** The worker body: edges for every item; an item that throws is left out (no edges for that mesh). */
export function computeLightModelEdges(request: LightModelEdgesRequest): LightModelEdgesResponse {
    const results: LightModelEdgesResultItem[] = [];
    for (const item of request?.items ?? []) {
        try {
            results.push({ id: item.id, positions: computeEdgePositions(item.position, item.index, request.thresholdAngleDeg) });
        } catch {
            // Malformed geometry: no edges for this mesh, keep the others
        }
    }
    return { jobId: request?.jobId, results };
}
